/** Rótulo curto na impressão do receituário (Receita A/B, lista C1…). */

import type { SncrReceitaTipo } from '../services/sncrService';

const CLASS_TO_TIPO: Record<string, SncrReceitaTipo> = {
  A1: 'NRA',
  A2: 'NRA',
  A3: 'NRA',
  B1: 'NRB',
  B2: 'NRB2',
  C1: 'RCE',
  C2: 'NRR',
  C3: 'NRT',
  C4: 'RCE',
  C5: 'RCE',
};

const TIPO_SHORT: Record<SncrReceitaTipo, string> = {
  NRA: 'Receita A',
  NRB: 'Receita B',
  NRB2: 'Receita B2',
  NRR: 'Receita C2',
  NRT: 'Receita C3',
  RCE: 'Controle especial',
  RET: 'Antimicrobiano',
};

function isSncrTipo(value: unknown): value is SncrReceitaTipo {
  return ['RCE', 'RET', 'NRA', 'NRB', 'NRB2', 'NRR', 'NRT'].includes(String(value || '').toUpperCase());
}

export function normalizeControlClass(raw: unknown): string | null {
  const text = String(raw || '').toUpperCase().replace(/[\s\-_.]/g, '');
  const match = text.match(/([ABCD])([1-5])/);
  return match ? `${match[1]}${match[2]}` : null;
}

function extractControlClasses(raw: unknown): string[] {
  const text = String(raw || '').toUpperCase();
  const re = /([ABCD])\s*[-_.]?\s*([1-5])/g;
  const out: string[] = [];
  const seen = new Set<string>();
  let m = re.exec(text);
  while (m) {
    const code = `${m[1]}${m[2]}`;
    if (!seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
    m = re.exec(text);
  }
  return out;
}

export const SNCR_TIPO_ORDER: SncrReceitaTipo[] = ['RCE', 'RET', 'NRA', 'NRB', 'NRB2', 'NRR', 'NRT'];
export const SNCR_NOTIFICATION_TIPOS: SncrReceitaTipo[] = ['NRA', 'NRB', 'NRB2', 'NRR', 'NRT'];

const SNCR_TIPO_PRIORITY: SncrReceitaTipo[] = ['NRA', 'NRB', 'NRB2', 'NRR', 'NRT', 'RCE', 'RET'];

function sncrTipoPriority(tipo: SncrReceitaTipo): number {
  const idx = SNCR_TIPO_PRIORITY.indexOf(tipo);
  return idx >= 0 ? idx : SNCR_TIPO_PRIORITY.length;
}

/** Tipo do receituário antigo quando o item controlado veio sem classe. */
export function tipoFromPrescriptionType(prescriptionType?: string | null): SncrReceitaTipo | null {
  const t = String(prescriptionType || '').toLowerCase();
  if (t === 'controlled_yellow') return 'NRA';
  if (t === 'controlled_blue') return 'NRB';
  if (t === 'antimicrobial') return 'RET';
  if (['special_control', 'special_white', 'dental'].includes(t)) return 'RCE';
  return null;
}

/** Mesma regra do backend (`sncrPrescription.ts`). */
export function resolveItemSncrTipo(
  item: {
    is_controlled?: boolean | null;
    control_class?: string | null;
    is_antimicrobial?: boolean | null;
    sncr_receita_tipo?: string | null;
  } | null | undefined,
  prescriptionType?: string | null,
): SncrReceitaTipo | null {
  if (!item) return null;

  const classTipos = new Set(
    extractControlClasses(item.control_class).map((c) => CLASS_TO_TIPO[c]).filter(Boolean),
  );
  const fromClasses = SNCR_TIPO_PRIORITY.find((t) => classTipos.has(t)) ?? null;
  const cls = normalizeControlClass(item.control_class);
  const fromSingle = cls && CLASS_TO_TIPO[cls] ? CLASS_TO_TIPO[cls] : null;
  const derivedFromClass = fromClasses || fromSingle;

  const explicitRaw = String(item.sncr_receita_tipo || '').trim().toUpperCase();
  if (isSncrTipo(explicitRaw)) {
    if (derivedFromClass && derivedFromClass !== explicitRaw
      && sncrTipoPriority(derivedFromClass) < sncrTipoPriority(explicitRaw)) {
      return derivedFromClass;
    }
    return explicitRaw;
  }

  if (derivedFromClass) return derivedFromClass;
  if (item.is_controlled || cls) {
    const fallback = tipoFromPrescriptionType(prescriptionType);
    return fallback && fallback !== 'RET' ? fallback : 'RCE';
  }
  if (item.is_antimicrobial) return 'RET';
  return null;
}

/** Título e selo de cada documento impresso. */
export const PAPER_DOCUMENT_TITLES: Record<SncrReceitaTipo, { title: string; badge: string }> = {
  RCE: { title: 'RECEITUÁRIO DE CONTROLE ESPECIAL', badge: 'Controle Especial' },
  RET: { title: 'RECEITUÁRIO - ANTIMICROBIANO', badge: 'Antimicrobiano — retenção' },
  NRA: { title: 'NOTIFICAÇÃO DE RECEITA A', badge: 'Notificação A (amarela)' },
  NRB: { title: 'NOTIFICAÇÃO DE RECEITA B', badge: 'Notificação B (azul)' },
  NRB2: { title: 'NOTIFICAÇÃO DE RECEITA B2', badge: 'Notificação B2 (azul)' },
  NRR: { title: 'NOTIFICAÇÃO DE RECEITA ESPECIAL — RETINOIDES', badge: 'Retinoides sistêmicos (C2)' },
  NRT: { title: 'NOTIFICAÇÃO DE RECEITA DE TALIDOMIDA', badge: 'Talidomida (C3)' },
};

export const PAPER_NOTIFICATION_NOTICE =
  'Notificação de receita: retida pela farmácia e válida somente com a numeração da Vigilância Sanitária (talonário) ou do SNCR.';

/** Texto do selo vermelho ao lado do medicamento na impressão. */
export function prescriptionItemControlBadgeLabel(
  item: {
    is_controlled?: boolean | null;
    control_class?: string | null;
    is_antimicrobial?: boolean | null;
    sncr_receita_tipo?: string | null;
  } | null | undefined,
): string | null {
  if (!item?.is_controlled && !item?.is_antimicrobial) return null;

  const tipo = resolveItemSncrTipo(item);
  const cls = normalizeControlClass(item.control_class) || extractControlClasses(item.control_class)[0] || null;

  if (!tipo) return cls ? `Lista ${cls}` : 'Controlado';

  const base = TIPO_SHORT[tipo];
  if (cls && (tipo === 'NRA' || tipo === 'NRB' || tipo === 'NRB2' || tipo === 'RCE' || tipo === 'NRR' || tipo === 'NRT')) {
    return `${base} · ${cls}`;
  }
  return base;
}
